// ImportTimetable.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import {
  getClasses,
  getSubjects,
  getTeachers,
  saveTimetable,
  getTimetable,
} from '../services/firebaseService';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const periods = ['1', '2', '3', '4', '5', '6', '7'];

// Default shared subjects that allow a teacher to teach multiple classes in same period
const DEFAULT_SHARED_SUBJECTS = [
  'PT',
  'Games',
  'Karate',
  'Yoga',
  'Dance',
  'Music',
  'Assembly',
  'Activity',
  'Library',
  'Computer Lab',
];

const ImportTimetable = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [timetable, setTimetable] = useState({});
  const [originalTimetable, setOriginalTimetable] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allTimetablesCache, setAllTimetablesCache] = useState({});
  const [isLoadingTimetables, setIsLoadingTimetables] = useState(false);
  const [sharedSubjects, setSharedSubjects] = useState(DEFAULT_SHARED_SUBJECTS);

  // Load shared subjects from Firebase
  useEffect(() => {
    const sharedSubjectsRef = ref(db, 'sharedSubjects');
    onValue(sharedSubjectsRef, (snapshot) => {
      const data = snapshot.val();
      if (data && Array.isArray(data)) {
        setSharedSubjects(data);
      } else {
        setSharedSubjects(DEFAULT_SHARED_SUBJECTS);
      }
    });
  }, []);

  // Load classes, subjects, teachers on mount
  useEffect(() => {
    getClasses((data) => {
      console.log('Classes loaded:', data);
      setClasses(data || []);
    });

    getSubjects((data) => {
      console.log('Subjects loaded:', data);
      setSubjects(data || []);
    });

    getTeachers((data) => {
      console.log('Teachers loaded:', data);
      setTeachers(data || []);
    });

    // Load all timetables for conflict checking
    loadAllTimetables();
  }, []);

  // Load all timetables from Firebase
  const loadAllTimetables = useCallback(() => {
    setIsLoadingTimetables(true);
    getClasses((classesData) => {
      const allTimetables = {};
      let loadedCount = 0;
      
      if (!classesData || classesData.length === 0) {
        setIsLoadingTimetables(false);
        setAllTimetablesCache({});
        return;
      }

      classesData.forEach((cls) => {
        const classKey = `${cls.className}_${cls.section}`;
        getTimetable(classKey, (data) => {
          allTimetables[classKey] = data || {};
          loadedCount++;
          
          if (loadedCount === classesData.length) {
            setAllTimetablesCache(allTimetables);
            setIsLoadingTimetables(false);
            console.log('All timetables loaded:', allTimetables);
          }
        });
      });
    });
  }, []);

  // Load timetable when class changes
  useEffect(() => {
    if (!selectedClass) {
      setTimetable({});
      setOriginalTimetable({});
      setError('');
      return;
    }

    setLoading(true);
    getTimetable(selectedClass, (data) => {
      const parsed = data || {};
      setTimetable(parsed);
      setOriginalTimetable(JSON.parse(JSON.stringify(parsed)));
      setError('');
      setLoading(false);
    });
  }, [selectedClass]);

  // Check if a subject is a shared subject (can have multiple classes in same period)
  const isSharedSubject = useCallback((subjectName) => {
    if (!subjectName) return false;
    return sharedSubjects.some(s => 
      subjectName.toLowerCase().includes(s.toLowerCase()) ||
      s.toLowerCase().includes(subjectName.toLowerCase())
    );
  }, [sharedSubjects]);

  // Helper function to check if a teacher is busy at a specific day and period
  const isTeacherBusy = useCallback((teacherName, day, period, currentClass, subjectName) => {
    if (!allTimetablesCache || Object.keys(allTimetablesCache).length === 0) {
      return false;
    }

    // Check if subject is shared - if yes, teacher can teach multiple classes
    if (isSharedSubject(subjectName)) {
      return false; // Shared subjects allow multiple classes
    }

    // Check all timetables
    for (const [className, classTimetable] of Object.entries(allTimetablesCache)) {
      // Skip current class (we're checking other classes)
      if (className === currentClass) continue;
      
      const dayData = classTimetable[day] || {};
      const cell = dayData[period];
      
      if (cell && cell.teacher === teacherName) {
        return true;
      }
    }
    
    return false;
  }, [allTimetablesCache, isSharedSubject]);

  // Get available teachers for a specific subject and day/period
  const getAvailableTeachers = useCallback((subjectName, day, period, currentClass) => {
    if (!subjectName) {
      return { available: [], busy: [] };
    }
    
    // First, get all teachers who teach this subject
    const subjectTeachers = teachers.filter((t) => 
      t.status === 'Active' && 
      t.subjects && 
      Array.isArray(t.subjects) && 
      t.subjects.includes(subjectName)
    );
    
    // Then filter out busy teachers (only those teaching the same day+period in another class)
    // For shared subjects, teachers are never marked as busy
    const availableTeachers = subjectTeachers.filter((teacher) => {
      const isBusy = isTeacherBusy(teacher.name, day, period, currentClass, subjectName);
      return !isBusy;
    });
    
    // Get busy teachers for display
    const busyTeachers = subjectTeachers.filter((teacher) => {
      return isTeacherBusy(teacher.name, day, period, currentClass, subjectName);
    });
    
    return {
      available: availableTeachers || [],
      busy: busyTeachers || []
    };
  }, [teachers, isTeacherBusy]);

  // Update a cell
  const updateCell = (day, period, field, value) => {
    setError('');
    const newTimetable = { ...timetable };
    if (!newTimetable[day]) newTimetable[day] = {};
    if (!newTimetable[day][period]) newTimetable[day][period] = { subject: '', teacher: '' };

    if (field === 'subject') {
      newTimetable[day][period].subject = value;
      newTimetable[day][period].teacher = '';
      
      // Auto-select if only one available teacher
      const { available } = getAvailableTeachers(value, day, period, selectedClass);
      if (available && available.length === 1) {
        newTimetable[day][period].teacher = available[0].name;
      }
    } else if (field === 'teacher') {
      newTimetable[day][period].teacher = value;
    }

    setTimetable(newTimetable);
  };

  // Save timetable
  const handleSave = async () => {
    if (!selectedClass) {
      setError('Please select a class first.');
      return;
    }

    // Validate all cells: every subject must have a teacher
    for (const day of days) {
      const dayData = timetable[day] || {};
      for (const period of periods) {
        const cell = dayData[period];
        if (cell && cell.subject && !cell.teacher) {
          setError(`Missing teacher for ${day} Period ${period} (${cell.subject})`);
          return;
        }
        if (cell && !cell.subject && cell.teacher) {
          setError(`Missing subject for ${day} Period ${period}`);
          return;
        }
      }
    }

    // Check for teacher conflicts across different classes (same day + same period)
    // For shared subjects, skip the conflict check
    for (const day of days) {
      const dayData = timetable[day] || {};
      for (const period of periods) {
        const cell = dayData[period];
        if (cell && cell.teacher && cell.subject) {
          // Check if this teacher is busy in another class at this day/period
          // Skip check for shared subjects
          const isBusy = isTeacherBusy(cell.teacher, day, period, selectedClass, cell.subject);
          if (isBusy) {
            // Find which class is using this teacher
            let busyClass = '';
            for (const [className, classTimetable] of Object.entries(allTimetablesCache)) {
              if (className === selectedClass) continue;
              const dayData = classTimetable[day] || {};
              const cellData = dayData[period];
              if (cellData && cellData.teacher === cell.teacher) {
                busyClass = className.replace(/_/g, ' ');
                break;
              }
            }
            setError(`Teacher "${cell.teacher}" is already assigned to ${busyClass} at ${day} Period ${period}`);
            return;
          }
        }
      }
    }

    setLoading(true);
    try {
      await saveTimetable(selectedClass, timetable);
      setOriginalTimetable(JSON.parse(JSON.stringify(timetable)));
      setError('✅ Timetable saved successfully!');
      
      // Refresh all timetables cache after saving
      loadAllTimetables();
      
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save timetable.');
    } finally {
      setLoading(false);
    }
  };

  // Reset to last saved
  const handleReset = () => {
    setTimetable(JSON.parse(JSON.stringify(originalTimetable)));
    setError('');
  };

  // Render a single cell
  const renderCell = (day, period) => {
    const dayData = timetable[day] || {};
    const cell = dayData[period] || { subject: '', teacher: '' };
    const selectedSubject = cell.subject || '';
    const selectedTeacher = cell.teacher || '';

    const activeSubjects = subjects.filter((s) => s.status === 'Active');
    
    // Get available and busy teachers for this subject/day/period
    const { available, busy } = getAvailableTeachers(selectedSubject, day, period, selectedClass);

    const isShared = isSharedSubject(selectedSubject);

    return (
      <div style={styles.cell}>
        <select
          value={selectedSubject}
          onChange={(e) => updateCell(day, period, 'subject', e.target.value)}
          style={styles.select}
          disabled={loading || isLoadingTimetables}
        >
          <option value="">Select Subject</option>
          {activeSubjects.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name} {s.code ? `(${s.code})` : ''}
              {isSharedSubject(s.name) && ' (Shared)'}
            </option>
          ))}
        </select>
        <select
          value={selectedTeacher}
          onChange={(e) => updateCell(day, period, 'teacher', e.target.value)}
          style={{ ...styles.select, marginTop: '4px' }}
          disabled={!selectedSubject || loading || isLoadingTimetables}
        >
          <option value="">Select Teacher</option>
          {available && available.length > 0 && (
            <optgroup label={isShared ? "Available Teachers (Can teach multiple classes)" : "Available Teachers"}>
              {available.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          )}
          {busy && busy.length > 0 && (
            <optgroup label="Busy Teachers">
              {busy.map((t) => {
                // Find which class this teacher is teaching at this day/period
                let busyClass = '';
                for (const [className, classTimetable] of Object.entries(allTimetablesCache)) {
                  if (className === selectedClass) continue;
                  const dayData = classTimetable[day] || {};
                  const cellData = dayData[period];
                  if (cellData && cellData.teacher === t.name) {
                    busyClass = className.replace(/_/g, ' ');
                    break;
                  }
                }
                return (
                  <option key={t.id} value={t.name} disabled style={{ color: '#999' }}>
                    {t.name} (Busy in {busyClass})
                  </option>
                );
              })}
            </optgroup>
          )}
        </select>
        {isShared && selectedSubject && (
          <div style={styles.sharedBadge}>
            ⚡ Multiple classes can share this subject
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📚 Timetable Editor</h1>
        <div style={styles.selectorWrapper}>
          <label style={styles.label}>Select Class</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            style={styles.classSelect}
            disabled={loading || isLoadingTimetables}
          >
            <option value="">— Choose —</option>
            {classes
              .filter((cls) => cls.status === 'Active')
              .map((cls) => {
                const classKey = `${cls.className}_${cls.section}`;
                return (
                  <option key={cls.id} value={classKey}>
                    Class {cls.className} - {cls.section}
                  </option>
                );
              })}
          </select>
        </div>
      </div>

      {error && (
        <div style={{ 
          ...styles.error, 
          ...(error.includes('successfully') || error.includes('✅') ? styles.success : {})
        }}>
          {error}
        </div>
      )}

      {(loading || isLoadingTimetables) && (
        <div style={styles.loading}>
          <span>{isLoadingTimetables ? 'Loading all timetables...' : 'Loading timetable...'}</span>
        </div>
      )}

      {selectedClass && !loading && !isLoadingTimetables && (
        <div style={styles.tableWrapper}>
          <div style={styles.grid}>
            {/* Header row */}
            <div style={{ ...styles.row, ...styles.headerRow }}>
              <div style={styles.cornerCell}>Day / Period</div>
              {days.map((d) => (
                <div key={d} style={styles.headerCell}>
                  {d}
                </div>
              ))}
            </div>

            {/* Period rows */}
            {periods.map((period) => (
              <div key={period} style={styles.row}>
                <div style={styles.periodLabel}>Period {period}</div>
                {days.map((day) => (
                  <div key={`${day}-${period}`} style={styles.cellWrapper}>
                    {renderCell(day, period)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={styles.buttonBar}>
            <button 
              onClick={handleSave} 
              style={{ ...styles.btn, ...styles.saveBtn }}
              disabled={loading || isLoadingTimetables}
            >
              💾 Save Timetable
            </button>
            <button 
              onClick={handleReset} 
              style={{ ...styles.btn, ...styles.resetBtn }}
              disabled={loading || isLoadingTimetables}
            >
              ↺ Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Inline Styles -----
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 20px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eef2f7',
  },
  title: {
    fontSize: '1.9rem',
    fontWeight: '600',
    color: '#0a1e3c',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  selectorWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  label: {
    fontWeight: '500',
    color: '#1f3a6b',
    fontSize: '0.95rem',
  },
  classSelect: {
    padding: '10px 24px 10px 16px',
    borderRadius: '40px',
    border: '1px solid #d3deed',
    backgroundColor: 'white',
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#0a1e3c',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '200px',
    transition: '0.2s',
  },
  error: {
    backgroundColor: '#fff1f0',
    color: '#b02b2b',
    padding: '12px 18px',
    borderRadius: '12px',
    marginBottom: '20px',
    borderLeft: '4px solid #d32f2f',
    fontWeight: '500',
  },
  success: {
    backgroundColor: '#e6f7e6',
    color: '#1e6b3b',
    borderLeftColor: '#2e7d32',
  },
  loading: {
    padding: '20px',
    textAlign: 'center',
    color: '#1f3a6b',
    fontWeight: '500',
  },
  tableWrapper: {
    overflowX: 'auto',
    marginTop: '8px',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '800px',
    border: '1px solid #e2e9f2',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '110px repeat(6, 1fr)',
    borderBottom: '1px solid #e9eef5',
    backgroundColor: '#ffffff',
  },
  headerRow: {
    backgroundColor: '#f8faff',
    fontWeight: '600',
    color: '#1f3a6b',
  },
  cornerCell: {
    padding: '14px 10px',
    fontWeight: '600',
    fontSize: '0.85rem',
    color: '#3a5a82',
    backgroundColor: '#f8faff',
    borderRight: '1px solid #e2e9f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCell: {
    padding: '14px 6px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#1f3a6b',
    borderRight: '1px solid #e2e9f2',
    backgroundColor: '#f8faff',
    letterSpacing: '0.3px',
  },
  periodLabel: {
    padding: '8px 6px',
    textAlign: 'center',
    fontWeight: '500',
    fontSize: '0.85rem',
    color: '#2a4a7a',
    backgroundColor: '#fafcff',
    borderRight: '1px solid #e2e9f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellWrapper: {
    padding: '6px 4px',
    borderRight: '1px solid #e2e9f2',
    backgroundColor: '#ffffff',
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '100px',
  },
  select: {
    padding: '6px 4px',
    borderRadius: '8px',
    border: '1px solid #d7e0ec',
    backgroundColor: 'white',
    fontSize: '0.8rem',
    fontWeight: '400',
    color: '#0b1f3b',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    transition: '0.15s',
  },
  sharedBadge: {
    fontSize: '0.65rem',
    color: '#7c3aed',
    backgroundColor: '#ede9fe',
    padding: '2px 8px',
    borderRadius: '12px',
    textAlign: 'center',
    marginTop: '2px',
  },
  buttonBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '14px',
    marginTop: '28px',
    flexWrap: 'wrap',
  },
  btn: {
    padding: '12px 30px',
    borderRadius: '40px',
    border: 'none',
    fontWeight: '600',
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: '0.2s',
    backgroundColor: '#eef3fc',
    color: '#1f3a6b',
  },
  saveBtn: {
    backgroundColor: '#0a1e3c',
    color: 'white',
    boxShadow: '0 4px 12px rgba(10,30,60,0.15)',
  },
  resetBtn: {
    backgroundColor: '#f0f4fe',
    color: '#1f3a6b',
    border: '1px solid #d3deed',
  },
};

export default ImportTimetable;